import {
  BlanketProduct,
  BlanketProductMeta,
  BlanketProductWeightType,
} from '@upright/common/model/input/blanket/blanket-product/types';
import { RevenueShareClass } from '@upright/common/model/input/companies/products/types';
import { MetricNature } from '@upright/common/model/input/types';
import { get, isNil, isNull } from 'lodash';

/** Checks if the product contains non-deafault values
 * This can be used to e.g. omit delete confirmation in the frontend
 */
export function isEmptyProduct(product: BlanketProduct): boolean {
  return (
    !product.title &&
    isNil(product.weightType) &&
    isNil(product.weight) &&
    isNil(product.taxonomyMapping) &&
    isNil(product.mappedProductInfo) &&
    isNil(product.description)
  );
}

/**
 * True for a row flagged as a segment that carries no taxonomy mapping of its own: a container that
 * is never an assessable product, whether or not it currently has children.
 *
 * This is the structural test the publish path uses to decide what gets matched, what gets a mixin
 * and what reaches the published product set. Segment status is a stored flag rather than "has no
 * children" because a segment whose last child was moved away is still a segment: read structurally
 * it looks like a leaf, and it would be matched and published as a product (PLT-5899).
 *
 * A row with a mapping is a product whatever the flag says. Blanketti has no segment concept, so an
 * analyst can map a flagged row there and nothing tells them the flag exists; letting the flag win
 * would silently drop that product from the published set with no way to undo it.
 *
 * This answers "is it a container", not "did the company declare this split". The second is
 * `isSegmentContainer` below (the disclosure tick), and the two must not be merged, or every
 * editor-created grouping would read as company-reported.
 *
 * Rows written before every segment was flagged can be segments without the flag. Callers therefore
 * keep their own has-children test and add this one on top of it; the PLT-5899 backfill flags the
 * unflagged segments it can identify.
 */
export function isSegmentRow(product: {
  meta?: Pick<BlanketProductMeta, 'isSegment'> | null;
  taxonomyMapping?: BlanketProduct['taxonomyMapping'];
}): boolean {
  return product.meta?.isSegment === true && isNil(product.taxonomyMapping);
}

/**
 * Whether a product is a business unit / revenue segment container: a top-level node whose revenue
 * share is a direct disclosure.
 *
 * This is what a *declared* segment looks like in the database. `meta.isSegment` is not a usable
 * test for that: it says a row is a container, not that anyone declared the split, and sets saved
 * before the segment editor flagged every segment carry it only on segments with their own IPR
 * attributes.
 *
 * Shared so the publish path, the upclose reference-segment export and the read-time segment
 * labelling all agree on what a segment is. Publishing additionally requires `weight !== null`
 * where it counts segments with *disclosed revenue*; that is a property of the revenue, not of
 * being a segment, so it stays at the call site.
 */
export function isSegmentContainer(product: Pick<BlanketProduct, 'parentId' | 'weightNature'>): boolean {
  return isNil(product.parentId) && product.weightNature === MetricNature.DIRECT_DISCLOSURE;
}

export const allBlanketProductWeightTypes = ['relative', 'auto_major', 'auto_medium', 'auto_minor'] as const;

export function toRevenueShareClass(weightType: BlanketProductWeightType | null): RevenueShareClass | undefined {
  switch (weightType) {
    case null:
      return undefined;
    case 'auto_major':
      return RevenueShareClass.MAJOR;
    case 'auto_medium':
      return RevenueShareClass.MEDIUM;
    case 'auto_minor':
      return RevenueShareClass.MINOR;
    // `relative` is an exact share rather than a class, so there is no class to convert it to.
    case 'relative':
    default:
      throw Error(`Can no convert weightType "${weightType}" to revenueShareClass`);
  }
}

export function toFriendlyLabel(type: BlanketProductWeightType | null) {
  const labelByType: Record<BlanketProductWeightType, string> = {
    relative: 'Relative',
    auto_major: 'Major',
    auto_medium: 'Medium',
    auto_minor: 'Minor',
  };
  const unknownLabel = 'Unknown';
  return isNull(type) ? unknownLabel : get(labelByType, type, type);
}
